import { useEffect, useRef, useState } from "react";
import { useInView, motion } from "framer-motion";

/**
 * CountUp — Animated number counting with spring physics.
 * Inspired by React Bits CountUp component.
 */
export default function CountUp({
  target = 0,
  duration = 2,
  suffix = "",
  prefix = "",
  className = "",
  decimals = 0,
  ...props
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const [count, setCount] = useState(target);

  useEffect(() => {
    if (!isInView) return;

    let start = 0;
    const end = target;
    const startTime = performance.now();
    const durationMs = duration * 1000;
    setCount(start);

    function easeOutExpo(t) {
      return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
    }

    function animate(now) {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / durationMs, 1);
      const easedProgress = easeOutExpo(progress);
      const current = start + (end - start) * easedProgress;

      setCount(current);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    }

    requestAnimationFrame(animate);
  }, [isInView, target, duration]);

  const formatted = decimals > 0 ? count.toFixed(decimals) : Math.round(count);
  const finalFormatted = decimals > 0 ? Number(target).toFixed(decimals) : Math.round(Number(target) || 0).toLocaleString('en-IN');

  return (
    <motion.span
      ref={ref}
      className={`font-mono tabular-nums ${className}`}
      aria-label={`${prefix}${finalFormatted}${suffix}`}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={isInView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.3 }}
      {...props}
    >
      <span aria-hidden="true">{prefix}{typeof formatted === 'number' ? formatted.toLocaleString('en-IN') : formatted}{suffix}</span>
    </motion.span>
  );
}
